import React, { useState } from 'react';
import axios from 'axios';
import { Globe, Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fire-and-forget: record which language a reader picks (English/Spanish split).
export const logLanguagePick = (language, source) => {
  try {
    axios.post(`${BACKEND_URL}/api/analytics/language`, { language, source }, { withCredentials: true })
      .catch(() => {});
  } catch (_) { /* ignore */ }
};

// One-time, first-run language picker. Shows until the reader confirms a
// language; copy is intentionally bilingual since no choice exists yet.
const LanguageOnboarding = () => {
  const { needsLanguageChoice, confirmLanguageChoice, language } = useLanguage();
  const [selected, setSelected] = useState(language === 'es' ? 'es' : 'en');

  if (!needsLanguageChoice) return null;

  const handleContinue = () => {
    logLanguagePick(selected, 'onboarding');
    confirmLanguageChoice(selected);
  };

  const OPTIONS = [
    { id: 'en', native: 'English', sub: 'Continue in English' },
    { id: 'es', native: 'Español', sub: 'Continuar en español' },
  ];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/70 backdrop-blur-sm p-4"
      data-testid="language-onboarding"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md rounded-2xl bg-card border border-border shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="bg-gradient-to-r from-purple-700 to-indigo-700 px-6 py-7 text-white text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-white/15 flex items-center justify-center mb-3">
            <Globe className="w-6 h-6" />
          </div>
          <h2 className="text-xl font-bold leading-tight">Welcome to Lumera</h2>
          <p className="text-sm text-white/80">Bienvenido a Lumera</p>
        </div>

        <div className="px-6 py-6">
          <p className="text-center text-sm font-medium mb-1">Choose your language</p>
          <p className="text-center text-sm text-muted-foreground mb-5">Elige tu idioma</p>

          <div className="space-y-3">
            {OPTIONS.map((o) => {
              const active = selected === o.id;
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => setSelected(o.id)}
                  data-testid={`language-option-${o.id}`}
                  className={`w-full flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition-colors ${
                    active
                      ? 'border-purple-600 bg-purple-50 dark:bg-purple-900/20'
                      : 'border-border hover:border-purple-300 hover:bg-accent'
                  }`}
                >
                  <div className="flex-1">
                    <p className="font-semibold">{o.native}</p>
                    <p className="text-xs text-muted-foreground">{o.sub}</p>
                  </div>
                  <span
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${
                      active ? 'border-purple-600 bg-purple-600 text-white' : 'border-muted-foreground/40'
                    }`}
                  >
                    {active && <Check className="w-4 h-4" />}
                  </span>
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={handleContinue}
            data-testid="language-onboarding-continue"
            className="mt-6 w-full rounded-xl bg-gradient-to-r from-purple-700 to-indigo-700 py-3 font-semibold text-white hover:from-purple-800 hover:to-indigo-800 transition-colors"
          >
            {selected === 'es' ? 'Continuar' : 'Continue'}
          </button>
          <p className="mt-3 text-center text-[11px] text-muted-foreground">
            {selected === 'es'
              ? 'Puedes cambiarlo cuando quieras desde los ajustes.'
              : 'You can change this anytime in settings.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LanguageOnboarding;
